import { Mark, mergeAttributes } from '@tiptap/core';

/**
 * A citation mark — the editor representation of a `<cite id="…">claim</cite>`
 * span. The claim text stays editable prose, but the mark travels with it, so a
 * user editing around or inside a citation can't strip its provenance.
 *
 * - Load: tiptap-markdown renders markdown → HTML → DOM, so `parseHTML` catches
 *   the `<cite id>` element and applies this mark (requires Markdown html:true).
 * - Save: the `markdown.serialize` spec re-emits `<cite id="…">…</cite>`, which
 *   the backend re-parses into structured_content.
 *
 * There is deliberately no command to CREATE a cite mark — citations originate
 * from the build; manual editing only preserves or removes them.
 */
const CiteMark = Mark.create({
  name: 'cite',
  // Don't extend the mark when typing at its boundary — a citation shouldn't
  // silently swallow adjacent text the user adds after it.
  inclusive: false,

  addAttributes() {
    return {
      factId: {
        default: null,
        parseHTML: (el) => el.getAttribute('id'),
        renderHTML: (attrs) => (attrs.factId ? { id: attrs.factId } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'cite[id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'cite',
      mergeAttributes(HTMLAttributes, {
        class: 'cite-chip',
        'data-fact-id': HTMLAttributes.id ?? '',
      }),
      0,
    ];
  },

  addStorage() {
    return {
      markdown: {
        serialize: {
          open: (_state: unknown, mark: { attrs: { factId: string } }) =>
            `<cite id="${mark.attrs.factId}">`,
          close: () => '</cite>',
        },
        parse: {
          // Handled by markdown-it (html:true) + parseHTML above.
        },
      },
    };
  },
});

export default CiteMark;
